import http from "../../../../../api/apiClient";
import { createLdsMemoryCache, createLdsRequestCache } from "../../../../../components/lds/cache";
import type {
  MutationProcessingResponse,
  MutationProcessingSetting,
  MutationProcessingUpdatePayload,
  MutationType,
} from "../types/mutationProcessing.types";

const API_PATH = "/admin/mutation-processing-policies";
const CACHE_TTL_MS = 60_000;

export const MUTATION_PROCESSING_SETTINGS_QUERY_KEY = "administration.mutation-processing.settings";

interface CacheEntry {
  data: MutationProcessingResponse;
  updatedAt: number;
}

const settingsCache = createLdsMemoryCache<CacheEntry>();
const loadSettingsOnce = createLdsRequestCache<MutationProcessingResponse>();

const normalizeSettings = (data: unknown): MutationProcessingResponse => {
  if (!Array.isArray(data)) return [];
  return data.filter((item): item is MutationProcessingSetting => {
    const candidate = item as Partial<MutationProcessingSetting>;
    return typeof candidate.mutationType === "string";
  });
};

export const hasMutationProcessingSettingsCache = () => {
  const cached = settingsCache.get();
  return Boolean(cached && Date.now() - cached.updatedAt < CACHE_TTL_MS);
};

export const getCachedMutationProcessingSettings = () => settingsCache.get()?.data ?? null;

export const setCachedMutationProcessingSettings = (settings: MutationProcessingResponse) => {
  settingsCache.set({ data: settings, updatedAt: Date.now() });
  return settings;
};

export const updateCachedMutationProcessingSetting = (
  mutationType: MutationType,
  updater: (setting: MutationProcessingSetting) => MutationProcessingSetting
) => {
  const current = settingsCache.get()?.data ?? [];
  const next = current.map((setting) => (setting.mutationType === mutationType ? updater(setting) : setting));
  setCachedMutationProcessingSettings(next);
  return next;
};

export const invalidateMutationProcessingSettingsCache = () => {
  settingsCache.clear();
};

export const fetchMutationProcessingSettings = async (): Promise<MutationProcessingResponse> => {
  const cached = settingsCache.get();
  if (cached && Date.now() - cached.updatedAt < CACHE_TTL_MS) return cached.data;

  return loadSettingsOnce(async () => {
    const { data } = await http.get(API_PATH);
    return setCachedMutationProcessingSettings(normalizeSettings(data));
  });
};

export const updateMutationProcessingSetting = async (
  mutationType: MutationType,
  payload: MutationProcessingUpdatePayload
): Promise<MutationProcessingSetting> => {
  const { data } = await http.put(`${API_PATH}/${encodeURIComponent(mutationType)}`, payload);
  const updated = data as MutationProcessingSetting;
  updateCachedMutationProcessingSetting(mutationType, () => updated);
  return updated;
};
